import {
  FINANCE_ENTRY_TYPE_LABELS,
  FINANCE_RECURRENCE_LABELS,
  type FinanceEntryTypeValue,
  formatCurrency,
  formatDate,
  MONTH_SHORT_LABELS,
} from '@crm/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  Input,
  Select,
  Spinner,
} from '@crm/ui';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Plus,
  Receipt,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import {
  BarChart,
  ConfirmDialog,
  EmptyState,
  ExportPdfButton,
  PageHeader,
  Pagination,
  PrintButton,
  StatCard,
} from '@/components';
import { useDocumentTitle } from '@/hooks';
import { useCan } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import {
  type FinanceBill,
  useDeleteFinanceBill,
  useFinanceBills,
  useFinanceForecast,
} from '../api';
import { BillDialog } from '../components/bill-dialog';

type TypeFilter = 'all' | FinanceEntryTypeValue;

const CHART_SERIES = [
  { key: 'receivable', label: 'A receber', color: 'bg-blue-500' },
  { key: 'payable', label: 'A pagar', color: 'bg-red-500' },
];

export function BillsPage() {
  useDocumentTitle('Cadastro de contas');

  const canWrite = useCan('finance', 'WRITE');

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [type, setType] = useState<TypeFilter>('all');
  const [status, setStatus] = useState<'all' | 'active' | 'inactive'>('active');
  const [year, setYear] = useState(() => new Date().getFullYear());
  /** So o celular usa: 1 = janeiro a junho, 2 = julho a dezembro. */
  const [semester, setSemester] = useState<1 | 2>(() => (new Date().getMonth() < 6 ? 1 : 2));
  /** O grafico tem o proprio filtro: da para olhar so o que sai ou so o que entra. */
  const [chartType, setChartType] = useState<TypeFilter>('all');

  const bills = useFinanceBills({
    page,
    perPage,
    search: search.trim() || undefined,
    type: type === 'all' ? undefined : type,
    status,
  });

  const forecast = useFinanceForecast({
    year,
    type: chartType === 'all' ? undefined : chartType,
  });

  const deleteBill = useDeleteFinanceBill();

  const [editing, setEditing] = useState<FinanceBill | null>(null);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<FinanceBill | null>(null);

  function openDialog(bill: FinanceBill | null) {
    setEditing(bill);
    setDialogOpen(true);
  }

  const months = forecast.data?.months ?? [];
  const chartData = months.map((month) => ({
    label: MONTH_SHORT_LABELS[month.month - 1] ?? String(month.month),
    values: { payable: month.payable, receivable: month.receivable },
  }));

  // No celular o ano inteiro vira doze barras de dois dedos: melhor meio ano
  // por vez, comecando pelo semestre em que estamos.
  const semestreData = semester === 1 ? chartData.slice(0, 6) : chartData.slice(6);

  const currentMonth = forecast.data?.currentMonth;
  const yearTotal = forecast.data?.yearTotal;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Receipt />}
        title="Cadastro de contas"
        description="As contas do salao - avulsas e recorrentes. Delas saem os vencimentos de contas a pagar e a receber."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <PrintButton />
            <ExportPdfButton title="Cadastro de contas" />
            {canWrite ? (
              <Button onClick={() => openDialog(null)}>
                <Plus className="h-4 w-4" />
                <span>
                  Cadastrar<span className="hidden sm:inline"> conta</span>
                </span>
              </Button>
            ) : null}
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 sm:gap-4">
        {/* Titulo curto: no cartao ele trunca, e o periodo ja esta na dica. */}
        <StatCard
          title="A pagar no mes"
          value={formatCurrency(currentMonth?.payable ?? 0)}
          hint="Previsao do mes corrente"
          icon={<ArrowDownCircle />}
        />
        <StatCard
          title="A receber no mes"
          value={formatCurrency(currentMonth?.receivable ?? 0)}
          hint="Previsao do mes corrente"
          icon={<ArrowUpCircle />}
        />
        {/* Os totais do ano so no desktop: no celular eles empurram o grafico
            e a lista para baixo, e o mes e o que se olha no balcao. */}
        <StatCard
          className="hidden sm:block"
          title="A pagar no ano"
          value={formatCurrency(yearTotal?.payable ?? 0)}
          hint={`Previsao dos doze meses de ${year}`}
          icon={<ArrowDownCircle />}
        />
        <StatCard
          className="hidden sm:block"
          title="A receber no ano"
          value={formatCurrency(yearTotal?.receivable ?? 0)}
          hint={`Previsao dos doze meses de ${year}`}
          icon={<ArrowUpCircle />}
        />
      </div>

      <Card variant="flush">
        {/* No celular o titulo fica em cima e os controles dividem a linha
            de baixo - lado a lado, o ano ficava espremido contra o select. */}
        {/* Titulo e o que o grafico mostra na mesma linha, o ano embaixo: o
            seletor com moldura propria pesava mais que o proprio grafico. */}
        <CardHeader className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base">Previsao mensal</CardTitle>

            <Select
              className="h-auto w-auto border-0 bg-transparent py-0 pl-0 pr-6 text-right text-sm font-medium text-primary shadow-none focus-visible:ring-0"
              aria-label="Tipo no grafico"
              value={chartType}
              onChange={(event) => setChartType(event.target.value as TypeFilter)}
            >
              <option value="all">A pagar e receber</option>
              <option value="PAYABLE">So a pagar</option>
              <option value="RECEIVABLE">So a receber</option>
            </Select>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label="Ano anterior"
              onClick={() => setYear((current) => current - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium">{year}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label="Proximo ano"
              onClick={() => setYear((current) => current + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {forecast.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-6 w-6 text-muted-foreground" />
            </div>
          ) : (
            <BarChart
              className="sm:hidden"
              aside={
                <div className="flex shrink-0 gap-3">
                  {([1, 2] as const).map((metade) => (
                    <button
                      key={metade}
                      type="button"
                      onClick={() => setSemester(metade)}
                      className={cn(
                        'text-xs transition-colors',
                        semester === metade
                          ? 'font-medium text-primary underline underline-offset-4'
                          : 'text-muted-foreground',
                      )}
                    >
                      {metade === 1 ? '1o semestre' : '2o semestre'}
                    </button>
                  ))}
                </div>
              }
              data={semestreData}
              series={
                chartType === 'all'
                  ? CHART_SERIES
                  : CHART_SERIES.filter((entry) =>
                      chartType === 'PAYABLE'
                        ? entry.key === 'payable'
                        : entry.key === 'receivable',
                    )
              }
              formatValue={formatCurrency}
            />
          )}

          {forecast.isLoading ? null : (
            <BarChart
              className="hidden sm:block"
              data={chartData}
              series={
                chartType === 'all'
                  ? CHART_SERIES
                  : CHART_SERIES.filter((entry) =>
                      chartType === 'PAYABLE'
                        ? entry.key === 'payable'
                        : entry.key === 'receivable',
                    )
              }
              formatValue={formatCurrency}
            />
          )}
        </CardContent>
      </Card>

      <Card variant="flush">
        <CardContent className="space-y-6">
          {/* Filtro e lista no mesmo quadro: sao a mesma ferramenta, e o espaco
            entre eles ja diz onde um acaba e o outro comeca. */}
          <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
            <Input
              placeholder="Buscar pela descricao"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />

            {/* Os dois seletores dividem a linha no celular: um embaixo do
                  outro, os filtros empurravam a lista para fora da tela. O
                  `sm:contents` dissolve o par de volta na grade. */}
            <div className="grid grid-cols-2 gap-3 sm:contents">
              <Select
                aria-label="Tipo"
                value={type}
                onChange={(event) => {
                  setType(event.target.value as TypeFilter);
                  setPage(1);
                }}
              >
                <option value="all">A pagar e receber</option>
                <option value="PAYABLE">A pagar</option>
                <option value="RECEIVABLE">A receber</option>
              </Select>

              <Select
                aria-label="Situacao"
                value={status}
                onChange={(event) => {
                  setStatus(event.target.value as 'all' | 'active' | 'inactive');
                  setPage(1);
                }}
              >
                <option value="active">Ativas</option>
                <option value="inactive">Inativas</option>
                <option value="all">Todas</option>
              </Select>
            </div>
          </div>

          {bills.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-6 w-6 text-muted-foreground" />
            </div>
          ) : bills.isError ? (
            <p className="text-sm text-destructive">{toErrorMessage(bills.error)}</p>
          ) : bills.data && bills.data.data.length > 0 ? (
            <ul className="divide-y divide-border">
              {bills.data.data.map((bill) => (
                <li
                  key={bill.id}
                  className="flex items-center gap-2 py-3 first:pt-0 last:pb-0 sm:gap-4"
                >
                  {/* A linha toda abre o cadastro da conta: no celular o alvo
                      e o registro inteiro, nao um icone no canto. */}
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left"
                    onClick={() => openDialog(bill)}
                  >
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{bill.description}</span>
                      <Badge variant={bill.type === 'PAYABLE' ? 'destructive' : 'secondary'}>
                        {FINANCE_ENTRY_TYPE_LABELS[bill.type]}
                      </Badge>
                      {/* A recorrencia so no desktop: no celular ela jogava o
                          nome da conta para uma linha e o selo para outra. */}
                      <Badge variant="outline" className="hidden sm:inline-flex">
                        {FINANCE_RECURRENCE_LABELS[bill.recurrence]}
                      </Badge>
                      {bill.isActive ? null : <Badge variant="outline">Inativa</Badge>}
                    </span>
                    {/* No celular so o proximo vencimento: categoria e unidade
                        estouravam a linha e viravam reticencias. */}
                    <span className="block truncate text-xs text-muted-foreground sm:hidden">
                      {bill.nextDueDate
                        ? `vence em ${formatDate(bill.nextDueDate)}`
                        : 'sem proximo vencimento'}
                    </span>
                    <span className="hidden truncate text-xs text-muted-foreground sm:block">
                      {bill.categoryName ?? 'Sem categoria'}
                      {bill.unitName ? ` - ${bill.unitName}` : ''}
                      {bill.nextDueDate
                        ? ` - proximo vencimento em ${formatDate(bill.nextDueDate)}`
                        : ' - sem proximo vencimento'}
                    </span>
                  </button>

                  <div className="flex shrink-0 items-center gap-2 sm:justify-end sm:gap-3">
                    <p className="font-semibold tabular-nums">{formatCurrency(bill.amount)}</p>

                    <ChevronRight
                      aria-hidden
                      className="h-5 w-5 shrink-0 text-muted-foreground sm:hidden"
                    />

                    {canWrite ? (
                      <div className="hidden items-center sm:flex">
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Editar"
                          onClick={() => openDialog(bill)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Excluir"
                          onClick={() => setPendingRemoval(bill)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon={<Receipt />} title="Nenhuma conta neste filtro" />
          )}

          <Pagination
            meta={bills.data?.meta}
            label="contas"
            onChange={setPage}
            perPage={perPage}
            onPerPageChange={(value) => {
              setPerPage(value);
              setPage(1);
            }}
          />
        </CardContent>
      </Card>

      <BillDialog
        open={isDialogOpen}
        bill={editing}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
      />

      <ConfirmDialog
        open={pendingRemoval !== null}
        title="Excluir conta do cadastro"
        message={`${pendingRemoval?.description ?? ''} sai do cadastro e os vencimentos futuros em aberto somem. O que ja foi baixado fica no historico.`}
        loading={deleteBill.isPending}
        onClose={() => setPendingRemoval(null)}
        onConfirm={() => {
          if (pendingRemoval) deleteBill.mutate(pendingRemoval.id);
          setPendingRemoval(null);
        }}
      />
    </div>
  );
}
